import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useUser } from '../context/UserContext';
import { FaHome, FaGraduationCap, FaQuestionCircle, FaChartLine, FaArrowLeft, FaArrowRight, FaCheckCircle, FaTrophy, FaComments, FaUsers, FaFlask } from 'react-icons/fa';
import AuthService from '../services/AuthService';
import CourseService from '../services/CourseService';
import ProgressService from '../services/ProgressService';
import TeacherCurriculumService from '../services/TeacherCurriculumService';
import toast from 'react-hot-toast';
import soundEffects from '../utils/soundEffects';
import { createRipple, createPulse, createSparkle, createGlow, createSuccessBurst, createAchievementBurst } from '../utils/vfx';
import { markdownToHtml } from '../utils/markdownToHtml';
import { parseWorkshopDoc } from '../utils/workshopFormat';
import LessonWorkshop from './LessonWorkshop';
import ProUpgradeCard from './ProUpgradeCard';
import BottomNav from './BottomNav';
import { requiredSeconds, isScrolledToEnd } from '../utils/lessonProgress';
import './Lesson.css';

const Lesson = () => {
  const navigate = useNavigate();
  const { courseId, lessonId } = useParams();
  const isTeacherLesson = !courseId; // If no courseId, it's a teacher lesson
  const { state, actions } = useUser();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [course, setCourse] = useState(null);
  const [lesson, setLesson] = useState(null);
  const [lessons, setLessons] = useState([]);
  const [currentLessonIndex, setCurrentLessonIndex] = useState(0);
  const [quiz, setQuiz] = useState(null);
  const [quizAttempts, setQuizAttempts] = useState([]);
  const [lessonStartTime, setLessonStartTime] = useState(null);
  const readingTimeIntervalRef = useRef(null);

  // ---- Completion gating -------------------------------------------------
  // Lessons complete themselves once the student has genuinely been through
  // them: reading lessons need scroll-to-end + a minimum dwell time,
  // workshops complete when the player reaches its final screen.
  const contentRef = useRef(null);
  const [readSeconds, setReadSeconds] = useState(0);
  const [reachedEnd, setReachedEnd] = useState(false);
  const completingRef = useRef(false); // guards against double-submits

  // Authentication check
  useEffect(() => {
    const checkAuth = async () => {
      try {
        if (!AuthService.isAuthenticated()) {
          navigate('/login', { replace: true });
          return;
        }

        if (!state.user) {
          navigate('/login', { replace: true });
          return;
        }

        setIsAuthenticated(true);
      } catch (error) {
        navigate('/login', { replace: true });
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, [navigate, state.user]);

  // Track reading time - start timer when lesson loads
  useEffect(() => {
    if (!isAuthenticated || !lesson) return;

    // Set start time when lesson is loaded
    const startTime = Date.now();
    let accumulatedTime = 0; // Track time spent on page
    let lastActiveTime = startTime; // Track when user was last active
    let isPageVisible = !document.hidden; // Track if page is visible

    setLessonStartTime(startTime);

    // Send reading time periodically (every 30 seconds) and on unmount
    const sendReadingTime = async (timeSpent) => {
      if (timeSpent > 0 && lesson?.id) {
        try {
          await ProgressService.recordReadingTime(lesson.id, timeSpent);
        } catch (error) {
          // Silently fail - don't interrupt user experience
          console.error('Failed to record reading time:', error);
        }
      }
    };

    // Handle page visibility changes (user switches tabs, minimizes window, etc.)
    const handleVisibilityChange = () => {
      const now = Date.now();
      if (document.hidden) {
        // Page became hidden - accumulate time up to now
        if (isPageVisible) {
          accumulatedTime += (now - lastActiveTime) / 1000;
          isPageVisible = false;
        }
      } else {
        // Page became visible - resume tracking
        isPageVisible = true;
        lastActiveTime = now;
      }
    };

    // Handle page unload (user navigates away) - accumulate time but don't send
    // The cleanup function will handle sending the final time
    const handleBeforeUnload = () => {
      const now = Date.now();
      if (isPageVisible) {
        accumulatedTime += (now - lastActiveTime) / 1000;
      }
    };

    // Send reading time every 30 seconds while on page
    readingTimeIntervalRef.current = setInterval(() => {
      if (isPageVisible) {
        const now = Date.now();
        accumulatedTime += (now - lastActiveTime) / 1000;
        lastActiveTime = now;
        
        // Send accumulated time
        if (accumulatedTime > 0 && lesson?.id) {
          sendReadingTime(accumulatedTime);
          accumulatedTime = 0; // Reset after sending
        }
      }
    }, 30000); // Every 30 seconds

    // Add event listeners
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('beforeunload', handleBeforeUnload);

    // Cleanup: send final reading time when component unmounts or lesson changes
    return () => {
      if (readingTimeIntervalRef.current) {
        clearInterval(readingTimeIntervalRef.current);
      }
      
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      
      // Send final reading time
      const now = Date.now();
      if (isPageVisible) {
        accumulatedTime += (now - lastActiveTime) / 1000;
      }
      
      if (accumulatedTime > 0 && lesson?.id) {
        sendReadingTime(accumulatedTime);
      }
    };
  }, [isAuthenticated, lesson?.id]);

  // Load course and lesson data
  useEffect(() => {
    if (!isAuthenticated) return;

    const loadData = async () => {
      try {
        setLoading(true);
        
        // Check if this is a teacher lesson
        if (isTeacherLesson) {
          // Load teacher lesson
          const lessonData = await TeacherCurriculumService.getStudentLesson(lessonId);
          const teacherLesson = lessonData.lesson;
          
          // Format teacher lesson to match expected structure
          const formattedLesson = {
            id: teacherLesson.id,
            title: teacherLesson.title,
            content: teacherLesson.content,
            xpValue: teacherLesson.xpValue || 50,
            completed: teacherLesson.completed || teacherLesson.isCompleted || false,
            isCompleted: teacherLesson.completed || teacherLesson.isCompleted || false
          };
          setLesson(formattedLesson);
          
          setLessons([formattedLesson]); // Single lesson array
          setCurrentLessonIndex(0);
          
          // Load quiz for this lesson if it exists (now returned with lesson data)
          if (lessonData.quiz) {
            setQuiz(lessonData.quiz);
            // Teacher quiz attempts are loaded with the quiz on the quiz page; don't call global lesson/attempts API with UUID
            setQuizAttempts([]);
          }
          
          // Play page transition sound
          soundEffects.playPageTransition();
        } else {
          // Load course details for global lesson
          const courseData = await CourseService.getCourse(courseId);
          setCourse(courseData.course); // API returns { course: {...} }
          
          // Flatten all lessons from units into a single array (3-tier structure)
          const allLessons = courseData.course.units?.flatMap(unit => unit.lessons) || [];
          setLessons(allLessons);
          
          // Find current lesson
          const lessonIndex = allLessons.findIndex(l => l.id === parseInt(lessonId));
          if (lessonIndex !== -1) {
            setCurrentLessonIndex(lessonIndex);
            const currentLesson = allLessons[lessonIndex];
            setLesson(currentLesson);
            
            // Load quiz for this lesson (DB quiz or generated quiz via "quiz-{lessonId}").
            // Interactive workshops carry their own checkpoints, so they never
            // fall back to a generated quiz — generating one from their JSON
            // content produced unreadable questions.
            const lessonIsWorkshop = !!parseWorkshopDoc(currentLesson.content);
            if (currentLesson.quizzes && currentLesson.quizzes.length > 0) {
              setQuiz(currentLesson.quizzes[0]);
            } else if (!lessonIsWorkshop && !currentLesson.contentLocked) {
              setQuiz({
                id: `quiz-${currentLesson.id}`,
                title: `${currentLesson.title || 'Lesson'} Quiz`,
                lessonId: currentLesson.id
              });
            } else {
              setQuiz(null);
            }
            
            // Load quiz attempts for this lesson
            loadQuizAttempts(currentLesson.id);
            
            // Play page transition sound
            soundEffects.playPageTransition();
          } else {
            toast.error('Lesson not found');
            navigate(`/course/${courseId}`);
          }
        }
      } catch (error) {
        console.error('Failed to load lesson:', error);
        toast.error(error.message || 'Failed to load lesson');
        navigate('/learn');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [isAuthenticated, courseId, lessonId, navigate, isTeacherLesson]);

  const handleCompleteLesson = async () => {
    console.log('[Lesson] handleCompleteLesson called');
    console.log('[Lesson] Lesson object:', lesson);
    console.log('[Lesson] Lesson ID:', lesson?.id);
    console.log('[Lesson] Lesson ID type:', typeof lesson?.id);
    console.log('[Lesson] Is completed:', lesson?.isCompleted);
    console.log('[Lesson] Completed:', lesson?.completed);
    
    if (!lesson) {
      console.log('[Lesson] Skipping - lesson is null');
      return;
    }
    
    // Check if already completed (using both properties for compatibility)
    if (lesson.isCompleted === true || lesson.completed === true) {
      console.log('[Lesson] Skipping - lesson is already completed');
      toast.info('This lesson is already completed');
      return;
    }

    try {
      let response;
      if (isTeacherLesson) {
        // Complete teacher lesson
        console.log('[Lesson] Calling TeacherCurriculumService to complete teacher lesson:', lesson.id);
        response = await TeacherCurriculumService.completeStudentLesson(lesson.id);
      } else {
        // Complete global lesson
        console.log('[Lesson] Calling ProgressService.completeLesson with:', lesson.id);
        response = await ProgressService.completeLesson(lesson.id);
      }
      console.log('[Lesson] Response received:', response);
      
      const xpEarned = response.xpEarned || 0;
      if (xpEarned > 0) {
        toast.success(`+${xpEarned} XP earned!`);
        soundEffects.playSuccess();
        
        // Update local state
        actions.completeLesson(lesson.id);
        actions.addXp(xpEarned);
        
        // Check if user leveled up
        const newLevel = response.newLevel || state.progress?.level || 1;
        if (newLevel > (state.progress?.level || 1)) {
          toast.success(`🎉 Level Up! You're now level ${newLevel}!`);
          soundEffects.playLevelUp();
          const completeButton = document.querySelector('.btn-primary');
          if (completeButton) {
            createAchievementBurst(completeButton);
          }
        } else {
          const completeButton = document.querySelector('.btn-primary');
          if (completeButton) {
            createSuccessBurst(completeButton);
          }
        }
        
        // Update lesson completion status
        setLesson({ ...lesson, isCompleted: true, completed: true });
        
        // Update lessons array
        const updatedLessons = lessons.map(l => 
          l.id === lesson.id ? { ...l, isCompleted: true, completed: true } : l
        );
        setLessons(updatedLessons);
        
        // Also update the course data to reflect the change (only for global lessons)
        if (course && !isTeacherLesson) {
          const updatedCourse = {
            ...course,
            units: course.units.map(unit => ({
              ...unit,
              lessons: unit.lessons.map(l => 
                l.id === lesson.id ? { ...l, isCompleted: true } : l
              )
            }))
          };
          setCourse(updatedCourse);
        }
      } else {
        toast.success('Lesson completed!');
        setLesson({ ...lesson, isCompleted: true, completed: true });
      }
    } catch (error) {
      console.error('[Lesson] Error in handleCompleteLesson:', error);
      console.error('[Lesson] Error message:', error.message);
      console.error('[Lesson] Error stack:', error.stack);
      
      // If lesson is already completed, update UI to reflect this
      if (error.message && error.message.includes('already completed')) {
        console.log('[Lesson] Lesson already completed - updating UI');
        setLesson({ ...lesson, isCompleted: true, completed: true });
        const updatedLessons = lessons.map(l => 
          l.id === lesson.id ? { ...l, isCompleted: true, completed: true } : l
        );
        setLessons(updatedLessons);
        if (course && !isTeacherLesson) {
          const updatedCourse = {
            ...course,
            units: course.units.map(unit => ({
              ...unit,
              lessons: unit.lessons.map(l => 
                l.id === lesson.id ? { ...l, isCompleted: true } : l
              )
            }))
          };
          setCourse(updatedCourse);
        }
        toast.success('Lesson already completed');
      } else if (error.message && /more time|too fast/i.test(error.message)) {
        // Server's anti-cheat clock hasn't elapsed yet (can happen after a
        // backend restart). Retry quietly instead of losing the student's XP.
        console.log('[Lesson] Completion too early — will retry');
        completingRef.current = false;
        setTimeout(() => {
          if (!completingRef.current) {
            completingRef.current = true;
            handleCompleteLesson();
          }
        }, 20000);
      } else {
        toast.error(error.message || 'Failed to complete lesson');
        completingRef.current = false; // allow another attempt
      }
    }
  };

  // ---- Auto-completion ---------------------------------------------------

  const isWorkshopLesson = !!lesson && !lesson.contentLocked && !!parseWorkshopDoc(lesson.content);
  const secondsNeeded = lesson ? requiredSeconds(lesson.content) : 0;
  const alreadyDone = !!lesson && (lesson.isCompleted === true || lesson.completed === true);

  // Count time on the lesson (only while the tab is actually visible, so
  // leaving it open in a background tab doesn't earn credit).
  useEffect(() => {
    if (!lesson || alreadyDone || isWorkshopLesson || lesson.contentLocked) return;

    const tick = setInterval(() => {
      if (!document.hidden) setReadSeconds((s) => s + 1);
    }, 1000);
    return () => clearInterval(tick);
  }, [lesson, alreadyDone, isWorkshopLesson]);

  // Watch for the student reaching the end of the lesson body
  useEffect(() => {
    if (!lesson || alreadyDone || isWorkshopLesson || lesson.contentLocked) return;

    const check = () => {
      if (isScrolledToEnd(contentRef.current)) setReachedEnd(true);
    };
    check(); // short lessons may already be fully visible
    window.addEventListener('scroll', check, { passive: true });
    window.addEventListener('resize', check);
    return () => {
      window.removeEventListener('scroll', check);
      window.removeEventListener('resize', check);
    };
  }, [lesson, alreadyDone, isWorkshopLesson]);

  // Reset the gate when moving between lessons
  useEffect(() => {
    setReadSeconds(0);
    setReachedEnd(false);
    completingRef.current = false;
  }, [lessonId]);

  // Fire completion once both requirements are satisfied
  useEffect(() => {
    if (!lesson || alreadyDone || isWorkshopLesson || lesson.contentLocked) return;
    if (!reachedEnd || readSeconds < secondsNeeded) return;
    if (completingRef.current) return;

    completingRef.current = true;
    handleCompleteLesson();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reachedEnd, readSeconds, secondsNeeded, lesson, alreadyDone, isWorkshopLesson]);

  /** Called by the workshop player when the student reaches the end screen. */
  const handleWorkshopFinished = () => {
    if (alreadyDone || completingRef.current) return;
    completingRef.current = true;
    handleCompleteLesson();
  };

  const goToNextLesson = (e) => {
    if (currentLessonIndex < lessons.length - 1) {
      soundEffects.playClick();
      if (e) createRipple(e);
      const nextLesson = lessons[currentLessonIndex + 1];
      setTimeout(() => {
        navigate(`/course/${courseId}/lesson/${nextLesson.id}`);
      }, 200);
    }
  };

  const goToPreviousLesson = (e) => {
    if (currentLessonIndex > 0) {
      soundEffects.playClick();
      if (e) createRipple(e);
      const prevLesson = lessons[currentLessonIndex - 1];
      setTimeout(() => {
        navigate(`/course/${courseId}/lesson/${prevLesson.id}`);
      }, 200);
    }
  };

  const loadQuizAttempts = async (lessonId) => {
    try {
      const attempts = await ProgressService.getLessonQuizAttempts(lessonId);
      setQuizAttempts(attempts);
    } catch (error) {
      // Don't show error to user, just log it
    }
  };


  const handleTakeQuiz = (e) => {
    if (quiz) {
      soundEffects.playClick();
      createRipple(e);
      setTimeout(() => {
        if (isTeacherLesson) {
          navigate(`/teacher-quiz/${quiz.id}`);
        } else {
          navigate(`/course/${courseId}/lesson/${lessonId}/quiz/${quiz.id}`);
        }
      }, 200);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading lesson...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  if (!lesson) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Lesson not found</h2>
          <button
            onClick={() => navigate('/learn')}
            className="btn btn-primary"
          >
            ←
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 content-with-nav">
      {/* Top Bar */}
      <div className="bg-white shadow-sm border-b border-gray-200 px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <button 
              onClick={(e) => {
                soundEffects.playClick();
                createRipple(e);
                setTimeout(() => navigate(`/course/${courseId}`), 200);
              }}
              onMouseEnter={() => soundEffects.playHover()}
              className="text-gray-600 hover:text-gray-800"
            >
              <FaArrowLeft className="text-lg" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-gray-900">{course?.title}</h1>
              <p className="text-sm text-gray-600">Lesson {currentLessonIndex + 1} of {lessons.length}</p>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            <div className="text-right">
              <div className="text-sm font-medium text-gray-900">
                Level {state.progress?.level || 1}
              </div>
              <div className="text-xs text-gray-500">
                {state.progress?.xp || 0} XP
              </div>
            </div>
            <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
              <FaGraduationCap className="text-blue-600" />
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto p-4">
        {/* Lesson Content */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl p-8 shadow-sm mb-6"
        >
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900">{lesson.title}</h2>
            {lesson.isCompleted && (
              <div className="flex items-center space-x-2 text-green-600">
                <FaCheckCircle className="text-lg" />
                <span className="font-medium">Completed</span>
              </div>
            )}
          </div>

          {/* Lesson Content — interactive workshops render as a slide
              player; everything else renders as markdown/HTML like before */}
          {(() => {
            if (lesson.contentLocked) {
              return <ProUpgradeCard feature="This interactive workshop" />;
            }
            const workshopDoc = parseWorkshopDoc(lesson.content);
            if (workshopDoc) {
              return (
                <LessonWorkshop
                  doc={workshopDoc}
                  lessonId={lesson.id}
                  onFinish={handleWorkshopFinished}
                />
              );
            }
            return (
              <div
                ref={contentRef}
                className="lesson-content-html"
                dangerouslySetInnerHTML={{
                  __html: lesson.content ? markdownToHtml(lesson.content) : ''
                }}
              />
            );
          })()}
          
          {/* Fallback removed - markdownToHtml handles both markdown and HTML */}
          {false && (
            <div className="prose prose-lg max-w-none">
              {lesson.content && lesson.content.split('\n').map((line, index) => {
              // Clean up the line first
              let cleanLine = line.trim();
              
              // Note: Markdown headers (#, ##, ###, ####) are now converted to bold HTML in the database
              // So we don't need to handle them here anymore
              
              // Handle bullet points with labels (cleaned format)
              if (cleanLine.startsWith('- ') && cleanLine.includes(':')) {
                const content = cleanLine.substring(2);
                const colonIndex = content.indexOf(':');
                if (colonIndex > 0) {
                  const label = content.substring(0, colonIndex);
                  const rest = content.substring(colonIndex + 1).trim();
                  return (
                    <div key={index} className="flex mb-3">
                      <span className="font-semibold text-gray-900 mr-2 min-w-fit">• {label}:</span>
                      <span className="text-gray-700">{rest}</span>
                    </div>
                  );
                }
              } 
              // Handle regular bullet points
              else if (cleanLine.startsWith('- ')) {
                const content = cleanLine.substring(2);
                return (
                  <div 
                    key={index} 
                    className="ml-4 mb-3 text-gray-700"
                  >
                    • {content}
                  </div>
                );
              } 
              // Handle standalone bold text
              else if (cleanLine.startsWith('**') && cleanLine.endsWith('**') && cleanLine.length > 4) {
                return <div key={index} className="font-semibold text-gray-900 mb-4">{cleanLine.substring(2, cleanLine.length - 2)}</div>;
              } 
              // Handle empty lines
              else if (cleanLine === '') {
                return <div key={index} className="mb-3"></div>;
              } 
              // Handle tables - improved table rendering
              else if (cleanLine.startsWith('|')) {
                // Check if this is a table header or separator
                if (cleanLine.includes('---') || cleanLine.match(/^\|[\s\-\|]+\|$/)) {
                  return <div key={index} className="border-b border-gray-300 my-3"></div>;
                }
                
                // Parse table row
                const cells = cleanLine.split('|').slice(1, -1).map(cell => cell.trim());
                const isHeader = index > 0 && lesson.content.split('\n')[index - 1].trim().startsWith('|') && 
                                 !lesson.content.split('\n')[index - 1].includes('---');
                
                return (
                  <div key={index} className={`flex ${isHeader ? 'font-semibold bg-gray-50' : ''} mb-1`}>
                    {cells.map((cell, cellIndex) => (
                      <div key={cellIndex} className={`flex-1 p-3 ${cellIndex < cells.length - 1 ? 'border-r border-gray-200' : ''}`}>
                        {cell}
                      </div>
                    ))}
                  </div>
                );
              } 
              // Handle numbered lists
              else if (cleanLine.match(/^\d+\.\s/)) {
                const content = cleanLine.replace(/^\d+\.\s/, '');
                return (
                  <div 
                    key={index} 
                    className="ml-4 mb-3 text-gray-700"
                  >
                    {cleanLine.split('.')[0]}. {content}
                  </div>
                );
              }
              // Handle code blocks
              else if (cleanLine.startsWith('```')) {
                return <div key={index} className="bg-gray-100 p-4 rounded-lg my-4 font-mono text-sm"></div>;
              }
              // Handle regular paragraphs
              else {
                return (
                  <p 
                    key={index} 
                    className="text-gray-700 mb-4 leading-relaxed"
                  >
                    {cleanLine}
                  </p>
                );
              }
              })}
            </div>
          )}
        </motion.div>

        {/* Quiz Results Section */}
        {quizAttempts.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-xl p-6 shadow-sm mb-6"
          >
            <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
              <FaTrophy className="text-yellow-500 mr-2" />
              Quiz Results
            </h3>
            
            <div className="space-y-3">
              {quizAttempts.map((attempt, index) => (
                <div
                  key={attempt.id}
                  className={`flex items-center justify-between p-4 rounded-lg border ${
                    attempt.passed 
                      ? 'bg-green-50 border-green-200' 
                      : 'bg-red-50 border-red-200'
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                      attempt.passed 
                        ? 'bg-green-500 text-white' 
                        : 'bg-red-500 text-white'
                    }`}>
                      {attempt.score}%
                    </div>
                    <div>
                      <div className="font-medium text-gray-900">
                        Attempt #{quizAttempts.length - index}
                      </div>
                      <div className="text-sm text-gray-600">
                        {new Date(attempt.completedAt).toLocaleDateString()} at {new Date(attempt.completedAt).toLocaleTimeString()}
                      </div>
                      {attempt.timeSpent && (
                        <div className="text-xs text-gray-500">
                          Completed in {Math.floor(attempt.timeSpent / 60)}:{(attempt.timeSpent % 60).toString().padStart(2, '0')}
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="text-right">
                    <div className={`text-lg font-bold ${
                      attempt.passed ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {attempt.passed ? 'PASSED' : 'FAILED'}
                    </div>
                    {attempt.feedback && (
                      <div className="text-sm text-gray-600 mt-1">
                        {attempt.feedback}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
            
            {quizAttempts.length > 0 && (
              <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                <div className="text-sm text-blue-800">
                  <strong>Best Score:</strong> {Math.max(...quizAttempts.map(a => a.score))}%
                </div>
                <div className="text-sm text-blue-800">
                  <strong>Attempts:</strong> {quizAttempts.length}
                </div>
              </div>
            )}
          </motion.div>
        )}

        {/* Lesson Navigation */}
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={goToPreviousLesson}
            disabled={currentLessonIndex === 0}
            onMouseEnter={() => currentLessonIndex > 0 && soundEffects.playHover()}
            className={`flex items-center space-x-2 px-4 py-2 rounded-lg ${
              currentLessonIndex === 0
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'
            }`}
          >
            <FaArrowLeft className="text-sm" />
            <span>Previous</span>
          </button>

          <div className="flex items-center space-x-4">
            {/* No "complete" button — lessons finish themselves once the
                student has actually been through them. This shows what's
                still required. */}
            {!lesson.isCompleted && !lesson.contentLocked && (
              isWorkshopLesson ? (
                <div className="lesson-gate">
                  <span className="lesson-gate-label">
                    Finish the workshop to earn +{lesson.xpValue} XP
                  </span>
                </div>
              ) : (
                <div className="lesson-gate">
                  <div className="lesson-gate-bar">
                    <div
                      className="lesson-gate-fill"
                      style={{
                        width: `${Math.min(
                          100,
                          Math.round(
                            ((Math.min(readSeconds, secondsNeeded) / (secondsNeeded || 1)) * 0.5 +
                              (reachedEnd ? 0.5 : 0)) * 100
                          )
                        )}%`
                      }}
                    />
                  </div>
                  <span className="lesson-gate-label">
                    {!reachedEnd
                      ? `Read to the end to earn +${lesson.xpValue} XP`
                      : readSeconds < secondsNeeded
                      ? `Keep reading… ${Math.max(0, secondsNeeded - readSeconds)}s left`
                      : `Completing…`}
                  </span>
                </div>
              )
            )}

            {lesson.isCompleted && quiz && (
              <button
                onClick={handleTakeQuiz}
                onMouseEnter={(e) => {
                  soundEffects.playHover();
                  createGlow(e.currentTarget, '#10b981', 300);
                }}
                className="btn btn-secondary"
              >
                Take Quiz (+{quiz.xpValue} XP)
              </button>
            )}
            
            {/* Nothing to show when a completed lesson has no quiz — the
                student just moves on to the next lesson. */}
            
            <button
              onClick={goToNextLesson}
              disabled={currentLessonIndex === lessons.length - 1}
              onMouseEnter={() => currentLessonIndex < lessons.length - 1 && soundEffects.playHover()}
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg ${
                currentLessonIndex === lessons.length - 1
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'
              }`}
            >
              <span>Next</span>
              <FaArrowRight className="text-sm" />
            </button> 
          </div>
        </div>

        {/* Course Progress */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-xl p-6 shadow-sm"
        >
          <h3 className="font-semibold text-gray-900 mb-4">Course Progress</h3>
          <div className="space-y-2">
            {lessons.map((l, index) => (
              <div
                key={l.id}
                onClick={(e) => {
                  soundEffects.playClick();
                  createRipple(e);
                  setTimeout(() => navigate(`/course/${courseId}/lesson/${l.id}`), 200);
                }}
                onMouseEnter={(e) => {
                  soundEffects.playHover();
                  createPulse(e.currentTarget, 1.02, 200);
                }}
                className={`flex items-center space-x-3 p-3 rounded-lg cursor-pointer transition-all duration-200 hover:shadow-md ${
                  l.id === lesson.id
                    ? 'bg-blue-50 border border-blue-200'
                    : l.isCompleted
                    ? 'bg-green-50 border border-green-200 hover:bg-green-100'
                    : 'bg-gray-50 border border-gray-200 hover:bg-gray-100'
                }`}
              >
                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-sm font-medium ${
                  l.isCompleted
                    ? 'bg-green-500 text-white'
                    : l.id === lesson.id
                    ? 'bg-blue-500 text-white'
                    : 'bg-gray-300 text-gray-600'
                }`}>
                  {l.isCompleted ? <FaCheckCircle className="text-xs" /> : index + 1}
                </div>
                <div className="flex-1">
                  <div className="font-medium text-gray-900">{l.title}</div>
                  <div className="text-sm text-gray-500">
                    {l.isCompleted ? 'Completed - Click to revisit' : l.id === lesson.id ? 'Current' : 'Not started'}
                  </div>
                </div>
                {l.isCompleted && <FaCheckCircle className="text-green-500" />}
              </div>
            ))}
          </div>
        </motion.div>
      </div>

      {/* Bottom Navigation */}
      <BottomNav />
    </div>
  );
};

export default Lesson;
